import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { 
  Send, 
  Bot, 
  User, 
  Sparkles, 
  TrendingUp, 
  Wallet, 
  Target,
  BarChart3,
  ChevronRight,
  Loader2
} from 'lucide-react'
import { aiChatApi } from '../lib/api'
import { cn } from '../lib/utils'
import { useNavigate } from 'react-router-dom'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  structured?: any
  timestamp: Date
}

const suggestionCategories = [
  {
    icon: TrendingUp,
    label: 'Markets',
    queries: ['Find crypto markets with high liquidity', 'Markets closing this week'],
  },
  {
    icon: Wallet,
    label: 'Wallets',
    queries: ['Top performing whales', 'Wallets with 70%+ win rate'],
  },
  {
    icon: Target,
    label: 'Strategy',
    queries: ['How to calculate position size?', 'Explain Kelly Criterion'],
  },
  {
    icon: BarChart3,
    label: 'Analysis',
    queries: ['Markets with positive EV', 'Current market sentiment'],
  },
]

export default function AIChat() {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! I\'m your Polymarket trading assistant. I can help you find markets, analyze wallets, explain strategies, and identify opportunities. What would you like to know?',
      timestamp: new Date(),
    },
  ])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: suggestions } = useQuery({
    queryKey: ['aiSuggestions'],
    queryFn: () => aiChatApi.getSuggestions().then(r => r.data),
  })

  const chatMutation = useMutation({
    mutationFn: (message: string) => 
      aiChatApi.chat(message, { history: messages.map(m => ({ role: m.role, content: m.content })) }),
    onSuccess: (response) => {
      const aiMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: response.data.response,
        structured: response.data.structured,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, aiMessage])

      // Handle structured actions
      if (response.data.structured?.action === 'navigate') {
        setTimeout(() => {
          navigate(response.data.structured.path)
        }, 1500)
      }
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || chatMutation.isPending) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    chatMutation.mutate(input)
    setInput('')
  }

  const handleSuggestion = (query: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: query,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    chatMutation.mutate(query)
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">AI Trading Assistant</h1>
            <p className="text-sm text-gray-500">Powered by Kimi AI</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'flex',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={cn(
                'max-w-[80%] rounded-2xl px-4 py-3',
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              )}
            >
              <div className="flex items-center space-x-2 mb-1">
                {message.role === 'assistant' ? (
                  <>
                    <Bot className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-600">AI Assistant</span>
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4 text-blue-200" />
                    <span className="text-xs font-medium text-blue-200">You</span>
                  </>
                )}
              </div>
              
              <div className="text-sm whitespace-pre-wrap">{message.content}</div>

              {/* Structured action hint */}
              {message.structured?.action === 'navigate' && (
                <div className="mt-2 text-xs opacity-75 flex items-center">
                  <ChevronRight className="w-3 h-3 mr-1" />
                  Redirecting to {message.structured.path}...
                </div>
              )}
            </div>
          </div>
        ))}

        {chatMutation.isPending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl px-4 py-3">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-sm text-gray-600">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (shown when no input) */}
      {!input && messages.length < 3 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Try asking about</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {suggestionCategories.map((cat) => {
              const Icon = cat.icon
              return (
                <button
                  key={cat.label}
                  onClick={() => handleSuggestion(cat.queries[0])}
                  className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all text-left"
                >
                  <Icon className="w-4 h-4 text-blue-600 mr-2" />
                  <div>
                    <p className="text-xs font-medium text-gray-900">{cat.label}</p>
                    <p className="text-[10px] text-gray-500 truncate">{cat.queries[0]}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <form onSubmit={handleSubmit} className="flex items-center space-x-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about markets, wallets, strategies..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={!input.trim() || chatMutation.isPending}
            className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-2 text-center">
          AI can make mistakes. Always verify important trading decisions.
        </p>
      </div>
    </div>
  )
}
